import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi, formatApiError } from "../../lib/adminApi";
import { ImageIcon, BookOpen, ArrowRight, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminDashboard() {
  const [stats, setStats] = useState({ projects: 0, journal: 0, residencias: 0, enclaves: 0 });
  const [reBusy, setReBusy] = useState(false);
  const [reResult, setReResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [allProj, journal] = await Promise.all([
          adminApi.get("/projects"),
          adminApi.get("/journal"),
        ]);
        const projects = allProj.data || [];
        setStats({
          projects: projects.length,
          residencias: projects.filter((p) => p.category === "residencias").length,
          enclaves: projects.filter((p) => p.category === "enclaves").length,
          journal: (journal.data || []).length,
        });
      } catch {
        /* ignore */
      }
    })();
  }, []);

  const runReoptimize = async () => {
    if (
      !window.confirm(
        "Esto recorrerá todos tus proyectos y notas, reprocesará las imágenes no optimizadas y actualizará las referencias. Puede tardar un par de minutos. ¿Continuar?"
      )
    )
      return;
    setReBusy(true);
    setReResult(null);
    try {
      const { data } = await adminApi.post("/admin/reoptimize");
      setReResult(data);
      toast.success(
        `Optimización completada: ${data.optimized} archivos · ${data.kb_saved} KB ahorrados`
      );
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setReBusy(false);
    }
  };

  return (
    <section className="px-10 py-12 max-w-[1100px]" data-testid="admin-dashboard">
      <div
        className="text-[var(--pc-bronze)]"
        style={{
          fontFamily: "Inter, sans-serif",
          fontWeight: 400,
          fontSize: 11,
          letterSpacing: "0.34em",
          textTransform: "uppercase",
        }}
      >
        Bienvenido
      </div>
      <h1
        className="headline-xl mt-3 text-[var(--pc-text)]"
        style={{ fontSize: "clamp(26px, 2.6vw, 38px)", letterSpacing: "0.16em", lineHeight: 1.2 }}
      >
        Panel de administración
      </h1>
      <div className="h-px w-10 bg-[var(--pc-bronze)] mt-6 opacity-90" />
      <p className="body-soft mt-8 max-w-[560px]" style={{ fontSize: 14.5 }}>
        Aquí puedes alimentar la web con nuevos proyectos —residencias y enclaves— y publicar notas en el Cuaderno.
        Los cambios se reflejan en el sitio público de inmediato.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12">
        <DashCard
          to="/admin/proyectos"
          title="Proyectos"
          subtitle={`${stats.projects} totales · ${stats.residencias} residencias · ${stats.enclaves} enclaves`}
          icon={<ImageIcon size={20} strokeWidth={1.2} />}
          testId="dash-projects"
        />
        <DashCard
          to="/admin/cuaderno"
          title="Cuaderno"
          subtitle={`${stats.journal} notas publicadas`}
          icon={<BookOpen size={20} strokeWidth={1.2} />}
          testId="dash-journal"
        />
      </div>

      {/* Re-optimize tool */}
      <div
        className="mt-14 border border-[var(--pc-line)] bg-[#0b0b0d] p-8"
        data-testid="reoptimize-card"
      >
        <div className="flex items-start gap-4">
          <div className="text-[var(--pc-bronze)] mt-1">
            <Sparkles size={20} strokeWidth={1.2} />
          </div>
          <div className="flex-1">
            <h3
              className="text-[var(--pc-text)]"
              style={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 300,
                fontSize: 18,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
              }}
            >
              Optimizar imágenes existentes
            </h3>
            <p
              className="mt-3 max-w-[640px] text-[var(--pc-text-soft)]"
              style={{ fontFamily: "Inter", fontSize: 13.5, letterSpacing: "0.02em", lineHeight: 1.7 }}
            >
              Recorre todos los proyectos y notas ya cargados, reprocesa las imágenes que aún no estén optimizadas
              (las convierte a WebP, máximo 2400&nbsp;px, calidad 82) y actualiza automáticamente las referencias.
              Las imágenes externas (URLs públicas) no se tocan.
            </p>
            <div className="mt-6">
              <button
                onClick={runReoptimize}
                disabled={reBusy}
                className="admin-btn-primary flex items-center gap-2"
                data-testid="reoptimize-btn"
              >
                {reBusy && <Loader2 size={14} className="animate-spin" />}
                <span>{reBusy ? "Optimizando..." : "Optimizar ahora"}</span>
              </button>
            </div>
            {reResult && (
              <div
                className="mt-6 text-[var(--pc-text-soft)] space-y-1"
                style={{ fontFamily: "Inter", fontSize: 12.5, letterSpacing: "0.04em" }}
                data-testid="reoptimize-result"
              >
                <div>Archivos escaneados: <span className="text-[var(--pc-text)]">{reResult.scanned}</span></div>
                <div>Optimizados: <span className="text-[var(--pc-bronze-bright)]">{reResult.optimized}</span></div>
                <div>Saltados (ya optimizados o externos): {reResult.skipped}</div>
                <div>Ahorro total: <span className="text-[var(--pc-bronze-bright)]">{reResult.kb_saved} KB</span></div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function DashCard({ to, title, subtitle, icon, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="block border border-[var(--pc-line)] bg-[#0b0b0d] p-8 hover:border-[var(--pc-bronze)] transition-colors duration-500 group"
    >
      <div className="flex items-start justify-between">
        <div className="text-[var(--pc-bronze)]">{icon}</div>
        <ArrowRight
          size={18}
          strokeWidth={1.2}
          className="text-[var(--pc-text-soft)] group-hover:text-[var(--pc-bronze-bright)] group-hover:translate-x-1 transition-all"
        />
      </div>
      <h3
        className="mt-8 text-[var(--pc-text)]"
        style={{
          fontFamily: "Inter, sans-serif",
          fontWeight: 300,
          fontSize: 22,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
        }}
      >
        {title}
      </h3>
      <p
        className="mt-3 text-[var(--pc-text-soft)]"
        style={{ fontFamily: "Inter, sans-serif", fontWeight: 300, fontSize: 13, letterSpacing: "0.08em" }}
      >
        {subtitle}
      </p>
    </Link>
  );
}
